"use client";

import { createChallenge } from "@/actions/match";
import { useAuth } from "@/contexts/AuthContext";
import {
  useMatchesContext,
  useTeamsContext,
  useTournamentContext,
} from "@/contexts/TournamentContext";
import { useCanChallenge } from "@/hooks/useCanChallenge";
import { useCooldownRemaining } from "@/hooks/useCooldownRemaining";
import { useCurrentTime } from "@/hooks/useCurrentTime";
import { VACATION_PROTECTION_DAYS } from "@/lib/constants";
import {
  formatCountdown,
  getUpcomingVacationDays,
  getVacationDaysRemaining,
  getVacationProgress,
  teamIsOnVacation,
} from "@/lib/utils";
import {
  ChallengeType,
  PublicUser,
  Team,
  TeamStatistics,
  TeamStatus,
} from "@/types";
import { useProgress } from "@bprogress/next";
import { cn } from "@heroui/theme";
import { addToast } from "@heroui/toast";
import { format } from "date-fns";
import { enUS, fi } from "date-fns/locale";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import { AchievementBadges } from "./AchievementBadges";
import { ChallengeButton } from "./ChallengeButton";
import { CooldownCounter } from "./team-info/CooldownCounter";
import { TeamCardPlayers } from "./team-info/TeamCardPlayers";
import { TeamCardStats } from "./team-info/TeamCardStats";

function BraveheartCountdown({ deadline }: { deadline: Date }) {
  const t = useTranslations();
  const now = useCurrentTime();
  const timeLeft = Math.max(0, new Date(deadline).getTime() - now);

  return (
    <div className="text-[0.6rem] sm:text-xs text-amber-400 mt-0.5">
      {timeLeft <= 0
        ? t("teams.braveheartAutoAccepted")
        : `${t("teams.braveheartResponseTime")} ${formatCountdown(timeLeft)}`}
    </div>
  );
}

interface TeamCardProps {
  team: Team;
  players: PublicUser[];
  isCurrentTeam: boolean;
  currentUserTeam: Team | null;
  isAboveMe: boolean;
  teamStats?: TeamStatistics;
  onTeamClick?: (team: Team) => void;
}

export function TeamCard({
  team,
  players,
  isCurrentTeam,
  currentUserTeam,
  isAboveMe,
  teamStats,
  onTeamClick,
}: TeamCardProps) {
  const t = useTranslations();
  const locale = useLocale();
  const { user } = useAuth();
  const { tournament } = useTournamentContext();
  const { teams } = useTeamsContext();
  const { matches } = useMatchesContext();
  const isOnVacation = teamIsOnVacation(team);
  const vacationDaysRemaining = isOnVacation
    ? getVacationDaysRemaining(team)
    : 0;
  const vacationProgress = isOnVacation ? getVacationProgress(team) : 0;
  // Guard ("Lomasuoja") window: the protected days right before a vacation
  // starts, during which the team can no longer be challenged. Computed from
  // the team's own vacation dates so it shows for every viewer (spectators
  // included), not only members whose own team drives useCanChallenge.
  const protectionDays =
    tournament?.vacationProtectionDays ?? VACATION_PROTECTION_DAYS;
  const guardDaysUntilVacation =
    !isOnVacation && tournament
      ? getUpcomingVacationDays(team, protectionDays)
      : null;
  const isInVacationGuard = guardDaysUntilVacation !== null;
  const { canChallenge, cardType, vacationStartsInDays } = useCanChallenge(
    currentUserTeam,
    team,
    tournament,
    teams,
    matches,
  );
  const progress = useProgress();
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isOnCooldown } = useCooldownRemaining(team, tournament);

  // Find active match and opponent team for this team
  const { activeMatch, opponentTeam } = useMemo(() => {
    const match = matches.find(
      (m) =>
        (m.team1Id === team.id || m.team2Id === team.id) &&
        (m.status === "challenge" || m.status === "scheduled"),
    );

    if (!match) return { activeMatch: null, opponentTeam: null };

    const opponentId =
      match.team1Id === team.id ? match.team2Id : match.team1Id;
    const opponent = teams.find((t) => t.id === opponentId);

    return { activeMatch: match, opponentTeam: opponent };
  }, [matches, team.id, teams]);

  // Map (status, isCurrentTeam, isOnVacation) → .fc-st-* modifier class.
  // CSS owns the actual colors via CSS variables.
  const getStatusModifier = () => {
    const actualStatus =
      isOnVacation || isInVacationGuard ? TeamStatus.ON_VACATION : team.status;
    if (actualStatus === TeamStatus.AVAILABLE) {
      return isCurrentTeam ? "fc-st-current" : "fc-st-available";
    }
    const map: Record<TeamStatus, string> = {
      [TeamStatus.AVAILABLE]: "fc-st-available",
      [TeamStatus.CHALLENGE]: "fc-st-challenge",
      [TeamStatus.MATCH_SCHEDULED]: "fc-st-scheduled",
      [TeamStatus.ON_VACATION]: "fc-st-vacation",
      [TeamStatus.INJURED]: "fc-st-injured",
      [TeamStatus.FORFEIT]: "fc-st-forfeit",
      [TeamStatus.JOIN_REQUEST]: "fc-st-disabled",
      [TeamStatus.DISABLED]: "fc-st-disabled",
    };
    return map[actualStatus] ?? "fc-st-available";
  };

  // Use pre-computed statistics (passed from parent to avoid N+1 query pattern)
  const stats = useMemo(() => {
    return {
      wins: teamStats?.wins ?? 0,
      losses: teamStats?.losses ?? 0,
    };
  }, [teamStats]);

  const handleChallenge = async (team: Team, cardType?: ChallengeType) => {
    if (!currentUserTeam || isSubmitting) return;

    setIsSubmitting(true);
    progress.start();

    try {
      const result = await createChallenge({
        team1Id: currentUserTeam.id,
        team2Id: team.id,
        challengeType: cardType,
      });

      if (!result.success) {
        console.error("Challenge failed:", result.error);
        addToast({
          title: t("teams.challengeFailed"),
          description: result.error,
          color: "danger",
        });
      } else {
        const isWalkover = team.status === TeamStatus.FORFEIT;
        addToast({
          title: isWalkover
            ? t("teams.walkoverWin", {
                teamName: team.name ?? t("common.unnamedTeam"),
              })
            : t("teams.challengeSent", {
                teamName: team.name ?? t("common.unnamedTeam"),
              }),
          color: "success",
        });
        // Auto-walkover challenges complete instantly with no chat thread —
        // skip the chat redirect.
        if (!isWalkover && result.matchId && tournament?.slug) {
          router.push(
            `/tournaments/${tournament.slug}/chat/${result.matchId}`,
          );
        }
      }
    } catch (error) {
      console.error("Challenge failed:", error);
      addToast({
        title: t("teams.challengeFailed"),
        color: "danger",
      });
    } finally {
      progress.stop();
      setIsSubmitting(false);
    }
  };

  const isBlockedBelow =
    !!currentUserTeam && !isAboveMe && !isCurrentTeam;
  const statusModifier = isBlockedBelow ? "fc-st-blocked" : getStatusModifier();

  return (
    <div
      data-my-team={isCurrentTeam}
      className={cn(
        "fc fc-tower",
        statusModifier,
        // Sizing — keep responsive widths from before
        "relative flex flex-shrink-0 cursor-pointer flex-col",
        "transition-transform duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)]",
        "hover:z-20 hover:scale-[1.02] hover:-translate-y-1 md:hover:-translate-y-2",
        "w-[calc(50%-6px)] max-w-[170px] min-h-[240px]",
        "sm:w-[155px] sm:max-w-none sm:min-h-[240px]",
        "md:w-[180px] md:min-h-[280px]",
        "lg:w-[200px] lg:min-h-[300px]",
        "xl:w-[210px]",
      )}
      onClick={() => onTeamClick?.(team)}
    >
      {/* Corner ornaments */}
      <svg className="fc-corner tl">
        <use href="#fc-cn" />
      </svg>
      <svg className="fc-corner tr">
        <use href="#fc-cn" />
      </svg>
      <svg className="fc-corner bl">
        <use href="#fc-cn" />
      </svg>
      <svg className="fc-corner br">
        <use href="#fc-cn" />
      </svg>

      {/* Challenge direction arrow - top left corner */}
      {(team.status === TeamStatus.CHALLENGE ||
        team.status === TeamStatus.MATCH_SCHEDULED) &&
        activeMatch && (
          <div
            className={cn(
              "absolute left-1 top-1 z-10 flex items-center justify-center",
              "h-5 w-5 sm:h-6 sm:w-6 rounded-full",
              "transition-transform duration-300",
              activeMatch.team1Id === team.id
                ? "bg-emerald-500/90"
                : "bg-amber-500/90",
            )}
            aria-hidden="true"
          >
            <span className="text-xs sm:text-sm text-white leading-none">
              {activeMatch.team1Id === team.id ? "↑" : "↓"}
            </span>
          </div>
        )}

      {/* Status emoji backgrounds - hidden on mobile for cleaner look */}
      {team.status !== TeamStatus.AVAILABLE &&
        team.status !== TeamStatus.DISABLED &&
        !isOnVacation && (
          <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-hidden rounded-xl md:rounded-2xl">
            <span
              className="text-[100px] md:text-[140px] opacity-10"
              aria-hidden="true"
            >
              {team.status === TeamStatus.MATCH_SCHEDULED && "🗓️"}
              {team.status === TeamStatus.INJURED && "🩹"}
              {team.status === TeamStatus.FORFEIT && "🏳️"}
              {team.status === TeamStatus.CHALLENGE && "⚔️"}
            </span>
          </div>
        )}
      {(isOnVacation || isInVacationGuard) && (
        <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-hidden rounded-xl md:rounded-2xl">
          <span
            className="text-[100px] md:text-[140px] opacity-10"
            aria-hidden="true"
          >
            {isOnVacation ? "🏖️" : "🛡️"}
          </span>
        </div>
      )}

      {/* Team name banner */}
      <div className="fc-banner">
        {team.name || t("common.unnamedTeam")}
      </div>

      {/* Player names */}
      <div className="fc-tower-body">
        <TeamCardPlayers
          players={players}
          captainId={team.captainId}
          hideNames={team.status === TeamStatus.FORFEIT}
        />
      </div>

      {/* Stats + achievements */}
      <div className="fc-stats">
        <TeamCardStats
          wins={stats.wins}
          losses={stats.losses}
          achievements={
            tournament ? (
              <AchievementBadges
                tournamentId={tournament.id}
                teamId={team.id}
                size="sm"
              />
            ) : null
          }
        />
      </div>
      {/* Bottom slot: challenge button OR status footer OR divider emblem */}
      {!isInVacationGuard &&
      (canChallenge || vacationStartsInDays !== undefined) &&
      (team.status === TeamStatus.AVAILABLE ||
        team.status === TeamStatus.FORFEIT) ? (
        <div className="fc-cta-bar">
          <ChallengeButton
            targetTeam={team}
            onChallenge={() => {
              handleChallenge(team, cardType as ChallengeType);
            }}
            cardType={cardType}
            isSubmitting={isSubmitting}
            disabled={vacationStartsInDays !== undefined}
            vacationStartsInDays={vacationStartsInDays}
            isWalkover={team.status === TeamStatus.FORFEIT}
          />
        </div>
      ) : team.status !== TeamStatus.AVAILABLE ||
        isOnVacation ||
        isInVacationGuard ||
        isCurrentTeam ||
        isOnCooldown ||
        (isCurrentTeam && tournament) ? (
        <div className="fc-cta-bar">
          <div className="w-full rounded-b-lg sm:rounded-b-xl md:rounded-b-2xl border-t border-white/10 bg-[var(--bg-stats)] px-1 py-1 sm:px-2 sm:py-1.5 md:px-3 md:py-2 flex flex-col items-center text-center justify-center font-cinzel text-xs sm:text-xs md:text-sm font-bold uppercase tracking-[0.05em] sm:tracking-[0.1em] text-default-500 min-h-[36px] sm:min-h-[44px] md:min-h-[52px]">
              {isOnVacation && (
                <>
                  <div className="line-clamp-1 text-amber-400">
                    {t("teams.status.on_vacation")}
                  </div>
                  <div className="text-[0.65rem] sm:text-xs md:text-xs font-normal normal-case tracking-normal text-amber-400/70">
                    {t("teams.vacationDaysRemaining", {
                      days: vacationDaysRemaining,
                    })}
                  </div>
                  {/* Elapsed-progress bar — fills up as the vacation runs out */}
                  <div
                    className="mt-1 h-1 w-full overflow-hidden rounded-full bg-amber-950/50"
                    role="progressbar"
                    aria-valuenow={Math.round(vacationProgress * 100)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={t("teams.status.on_vacation")}
                  >
                    <div
                      className="h-full rounded-full bg-amber-400/70"
                      style={{ width: `${Math.round(vacationProgress * 100)}%` }}
                    />
                  </div>
                </>
              )}
              {/* Vacation guard ("Lomasuoja"): protected days before the
                  vacation starts. Shown to every viewer, not just challengers. */}
              {isInVacationGuard && guardDaysUntilVacation !== null && (
                <>
                  <div className="line-clamp-1 text-amber-400">
                    🛡️ {t("teams.vacationGuard")}
                  </div>
                  <div className="text-[0.65rem] sm:text-xs md:text-xs font-normal normal-case tracking-normal text-amber-400/70">
                    {t("tower.vacationStartsInDays", {
                      days: guardDaysUntilVacation,
                    })}
                  </div>
                </>
              )}
              {!isOnVacation && team.status === TeamStatus.INJURED && (
                <div className="line-clamp-1">{t("teams.status.injured")}</div>
              )}

              {team.status === TeamStatus.FORFEIT && (
                <div className="line-clamp-1 text-rose-400">
                  {t("teams.status.forfeit")}
                </div>
              )}

              {team.status === TeamStatus.CHALLENGE &&
                opponentTeam &&
                activeMatch && (
                  <div className="text-xs sm:text-xs mt-1 text-white/90 font-normal normal-case tracking-normal leading-relaxed max-w-full">
                    <div className="line-clamp-2">
                      {activeMatch.team1Id === team.id
                        ? t("matches.challengedTeam", {
                            teamName:
                              opponentTeam.name || t("common.unnamedTeam"),
                            date: format(activeMatch.createdAt, "d.M."),
                          })
                        : t("matches.teamChallenged", {
                            teamName:
                              opponentTeam.name || t("common.unnamedTeam"),
                            date: format(activeMatch.createdAt, "d.M."),
                          })}
                    </div>
                    {activeMatch.braveheartResponseDeadline && (
                      <BraveheartCountdown
                        deadline={activeMatch.braveheartResponseDeadline}
                      />
                    )}
                  </div>
                )}

              {team.status === TeamStatus.MATCH_SCHEDULED &&
                opponentTeam &&
                activeMatch && (
                  <div className="text-xs sm:text-xs mt-1 text-white/90 font-normal normal-case tracking-normal leading-relaxed max-w-full">
                    <div className="line-clamp-1">
                      {t("matches.match")}{" "}
                      {activeMatch.matchDate &&
                        format(activeMatch.matchDate, "EEEEEE d.M. HH.mm", {
                          locale: locale === "fi" ? fi : enUS,
                        })}
                    </div>
                    {opponentTeam.name || t("common.unnamedTeam")}
                  </div>
                )}

              {/* Show "My team" label for current user's team when available */}
              {isCurrentTeam &&
                !isInVacationGuard &&
                team.status === TeamStatus.AVAILABLE && (
                <>
                  <div className="text-white">{t("teams.myTeam")}</div>
                  {tournament && (
                    <CooldownCounter
                      team={team}
                      tournament={tournament}
                      variant="compact"
                    />
                  )}
                </>
              )}
              {!isCurrentTeam &&
                !isOnVacation &&
                !isInVacationGuard &&
                team.status === TeamStatus.AVAILABLE &&
                vacationStartsInDays === undefined &&
                isOnCooldown &&
                tournament && (
                  <CooldownCounter
                    team={team}
                    tournament={tournament}
                    variant="compact"
                  />
                )}
          </div>
        </div>
      ) : (
        <div className="fc-divider">
          <svg className="fc-emblem">
            <use href="#fc-ts" />
          </svg>
        </div>
      )}
    </div>
  );
}
